/**
 * Utilitários para comunicação via WhatsApp
 * Funções reutilizáveis para construir mensagens e links do WhatsApp
 */

import { getProductFullUrl } from './productUrl';

/**
 * Verifica se está dentro do horário de atendimento
 * @returns {boolean} true se estiver dentro do horário de atendimento
 */
export const isBusinessHours = () => {
  const now = new Date();
  const day = now.getDay(); // 0 = Domingo, 6 = Sábado
  const hour = now.getHours();
  
  // Segunda a Sexta: 9h às 18h
  if (day >= 1 && day <= 5) {
    return hour >= 9 && hour < 18;
  }
  // Sábado: 9h às 13h
  if (day === 6) {
    return hour >= 9 && hour < 13;
  }
  // Domingo: fechado
  return false;
};

function formatBRL(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getCustomerGreeting(nomeCliente = 'visitante', isPT = true) {
  try {
    const { customerGreeting } = require('../config/whatsappMessages');
    return customerGreeting(nomeCliente, isPT);
  } catch {
    return isPT ? 'Olá, Emperc! 👋' : 'Hi, Emperc! 👋';
  }
}

/**
 * Remove apenas lixo de formatação — preserva emojis.
 * (A preservação de emoji depende de buildWhatsAppChatUrl usar Web/API, não wa.me nativo.)
 */
export function sanitizeWhatsAppText(text = '') {
  return String(text)
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Detecta ambiente desktop (onde o app nativo do WhatsApp costuma quebrar emoji no wa.me).
 */
export function isDesktopWhatsAppClient() {
  if (typeof navigator === 'undefined') return true;
  return !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || ''
  );
}

/**
 * Monta URL do chat com texto pré-preenchido, priorizando caminhos que
 * preservam UTF-8/emoji (Web no desktop; api.whatsapp.com no mobile).
 * Evita wa.me → app nativo Windows, que frequentemente vira � nos emojis.
 *
 * @param {string} phoneNumber - Número com DDI (ex.: 5521...). Se vazio, usa o padrão Emperc.
 * @param {string} text - Texto pré-preenchido
 */
export function buildWhatsAppChatUrl(phoneNumber, text = '') {
  const number = String(phoneNumber || '')
    .replace(/\D/g, '')
    .replace(/^0+/, '');
  const safeNumber = number || '5521998382460';
  const encoded = encodeURIComponent(sanitizeWhatsAppText(text));

  if (isDesktopWhatsAppClient()) {
    return `https://web.whatsapp.com/send?phone=${safeNumber}&text=${encoded}`;
  }

  return `https://api.whatsapp.com/send?phone=${safeNumber}&text=${encoded}`;
}

/**
 * Compartilhar texto no WhatsApp (usuário escolhe o contato — sem phone fixo).
 * Mesmo padrão Web/API para preservar emoji.
 */
export function buildWhatsAppShareUrl(text = '') {
  const encoded = encodeURIComponent(sanitizeWhatsAppText(text));

  if (isDesktopWhatsAppClient()) {
    return `https://web.whatsapp.com/send?text=${encoded}`;
  }

  return `https://api.whatsapp.com/send?text=${encoded}`;
}

/**
 * Formata itens do carrinho para mensagem do WhatsApp
 */
export const formatCartItemsForWhatsApp = (cartItems = [], isPT = true, maxItems = 5) => {
  if (!cartItems || cartItems.length === 0) return '';

  let texto = isPT ? `🛒 *Itens no carrinho:*\n` : `🛒 *Cart items:*\n`;

  cartItems.slice(0, maxItems).forEach((item) => {
    const itemTotal = Number(item.price) * Number(item.quantity || 1);
    texto += `• ${item.name} (${item.quantity}x) — ${formatBRL(itemTotal)}\n`;
  });

  if (cartItems.length > maxItems) {
    texto += isPT
      ? `… e mais ${cartItems.length - maxItems} item(ns)\n`
      : `… and ${cartItems.length - maxItems} more item(s)\n`;
  }

  const totalCarrinho = cartItems.reduce(
    (sum, item) => sum + Number(item.price) * Number(item.quantity || 1),
    0
  );
  const quantidadeItens = cartItems.reduce(
    (sum, item) => sum + Number(item.quantity || 1),
    0
  );

  texto += isPT
    ? `*Total:* ${formatBRL(totalCarrinho)} (${quantidadeItens} ${quantidadeItens === 1 ? 'item' : 'itens'})\n\n`
    : `*Total:* ${formatBRL(totalCarrinho)} (${quantidadeItens} ${quantidadeItens === 1 ? 'item' : 'items'})\n\n`;

  return texto;
};

/**
 * Mensagem base — sempre na voz do cliente para a Emperc
 */
export const buildWhatsAppMessage = ({
  nomeCliente = 'visitante',
  isPT = true,
  cartItems = [],
  currentUrl = '',
  filtros = {},
  categories = [],
  contextoAdicional = '',
} = {}) => {
  const temItensNoCarrinho = cartItems && cartItems.length > 0;
  let mensagemTexto = `${getCustomerGreeting(nomeCliente, isPT)}\n\n`;

  if (contextoAdicional) {
    mensagemTexto += `${contextoAdicional}\n\n`;
  } else {
    mensagemTexto += isPT
      ? `Gostaria de atendimento sobre peças da Emperc.\n\n`
      : `I'd like help with Emperc parts.\n\n`;
  }

  if (temItensNoCarrinho) {
    mensagemTexto += formatCartItemsForWhatsApp(cartItems, isPT);
  }

  const { selectedBrand, selectedCategory, minPrice, maxPrice } = filtros || {};
  const temFiltros = selectedBrand || selectedCategory || minPrice || maxPrice;

  if (temFiltros) {
    const filtrosTexto = [];
    if (selectedBrand) filtrosTexto.push(`Marca: ${selectedBrand}`);
    if (selectedCategory) {
      const categoriaNome =
        categories.find((c) => (c.slug || c._id) === selectedCategory)?.name ||
        selectedCategory;
      filtrosTexto.push(`Categoria: ${categoriaNome}`);
    }
    if (minPrice) filtrosTexto.push(`Preço mín.: ${formatBRL(minPrice)}`);
    if (maxPrice) filtrosTexto.push(`Preço máx.: ${formatBRL(maxPrice)}`);

    if (filtrosTexto.length > 0) {
      mensagemTexto += isPT
        ? `🔎 *Filtros que estou usando:*\n${filtrosTexto.join('\n')}\n\n`
        : `🔎 *Filters I'm using:*\n${filtrosTexto.join('\n')}\n\n`;
    }
  }

  if (currentUrl) {
    mensagemTexto += isPT
      ? `🔗 *Página:* ${currentUrl}\n\n`
      : `🔗 *Page:* ${currentUrl}\n\n`;
  }

  mensagemTexto += isPT ? `Podem me ajudar?` : `Can you help me?`;

  return sanitizeWhatsAppText(mensagemTexto);
};

/**
 * Gera link do WhatsApp com mensagem codificada
 */
export const generateWhatsAppLink = (options = {}, phoneNumber) => {
  const { getCachedWhatsAppNumber } = require('../services/siteContactApi');
  const number =
    phoneNumber || getCachedWhatsAppNumber() || '5521998382460';
  return buildWhatsAppChatUrl(number, buildWhatsAppMessage(options));
};

/**
 * Mensagem da página de produto (voz do cliente)
 */
export const buildProductWhatsAppMessage = (
  product,
  nomeCliente = 'visitante',
  isPT = true,
  detailed = true
) => {
  if (detailed) {
    try {
      const { buildDetailedProductMessage } = require('../config/whatsappMessages');
      return sanitizeWhatsAppText(
        buildDetailedProductMessage(product, nomeCliente, isPT)
      );
    } catch (e) {
      // fallback abaixo
    }
  }

  const productUrl = getProductFullUrl(product);
  let mensagemTexto = `${getCustomerGreeting(nomeCliente, isPT)}\n\n`;

  mensagemTexto += isPT
    ? `Tenho interesse nesta peça:\n\n`
    : `I'm interested in this part:\n\n`;

  mensagemTexto += `📦 *${product.name}*\n`;
  if (product.brand) {
    mensagemTexto += isPT
      ? `🏷️ *Marca:* ${product.brand}\n`
      : `🏷️ *Brand:* ${product.brand}\n`;
  }
  if (Number.isFinite(Number(product.price))) {
    mensagemTexto += isPT
      ? `💰 *Preço:* ${formatBRL(product.price)}\n`
      : `💰 *Price:* ${formatBRL(product.price)}\n`;
  }

  if (productUrl) {
    mensagemTexto += `\n🔗 ${productUrl}\n`;
  }

  mensagemTexto += isPT
    ? `\nPodem confirmar disponibilidade e compatibilidade?\n\nObrigado!`
    : `\nCan you confirm availability and compatibility?\n\nThank you!`;

  return sanitizeWhatsAppText(mensagemTexto);
};

/**
 * Tipos de números de WhatsApp disponíveis
 * Fonte de verdade: backend WHATSAPP_NUMBER via /api/public/contact
 */
export const WHATSAPP_NUMBERS = {
  VENDAS: '5521998382460',
  SUPORTE: '5521998382460',
  FINANCEIRO: '5521998382460',
  DEFAULT: '5521998382460',
};

/**
 * Tipos de contexto para diferentes páginas
 */
export const WHATSAPP_CONTEXT = {
  CATALOG: 'catalog',
  PRODUCT: 'product',
  CART: 'cart',
  CHECKOUT: 'checkout',
  ORDER: 'order'
};

/**
 * Determina o número de WhatsApp baseado no contexto
 * @param {string} context - Contexto da página (catalog, product, cart, checkout, order)
 * @param {string} customNumber - Número customizado (opcional)
 * @returns {string} Número do WhatsApp
 */
export const getWhatsAppNumber = (context = WHATSAPP_CONTEXT.CATALOG, customNumber = null) => {
  if (customNumber) return String(customNumber).replace(/\D/g, '') || customNumber;

  try {
    const { getCachedWhatsAppNumber } = require('../services/siteContactApi');
    return getCachedWhatsAppNumber();
  } catch {
    return WHATSAPP_NUMBERS.DEFAULT;
  }
};

/**
 * Mensagem da página de carrinho (voz do cliente)
 */
export const buildCartWhatsAppMessage = ({
  nomeCliente = 'visitante',
  isPT = true,
  cartItems = [],
  total = 0,
  contextoAdicional = '',
} = {}) => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Se veio um texto completo de follow-up e não há itens, usa-o direto
  if (contextoAdicional && (!cartItems || cartItems.length === 0)) {
    return sanitizeWhatsAppText(contextoAdicional);
  }

  let mensagemTexto = `${getCustomerGreeting(nomeCliente, isPT)}\n\n`;

  mensagemTexto += isPT
    ? `Tenho dúvidas sobre o meu *carrinho*:\n\n`
    : `I have questions about my *cart*:\n\n`;

  if (contextoAdicional) {
    mensagemTexto += `${contextoAdicional}\n\n`;
  }

  if (cartItems.length > 0) {
    mensagemTexto += formatCartItemsForWhatsApp(cartItems, isPT);
    if (Number.isFinite(Number(total)) && Number(total) > 0) {
      mensagemTexto += isPT
        ? `*Total do carrinho:* ${formatBRL(total)}\n\n`
        : `*Cart total:* ${formatBRL(total)}\n\n`;
    }
  }

  if (currentUrl) {
    mensagemTexto += isPT
      ? `🔗 *Página:* ${currentUrl}\n\n`
      : `🔗 *Page:* ${currentUrl}\n\n`;
  }

  mensagemTexto += isPT
    ? `Podem me ajudar com disponibilidade, pagamento ou frete?\n\nObrigado!`
    : `Can you help with availability, payment or shipping?\n\nThank you!`;

  return sanitizeWhatsAppText(mensagemTexto);
};

/**
 * Mensagem da página de checkout (voz do cliente)
 */
export const buildCheckoutWhatsAppMessage = ({
  nomeCliente = 'visitante',
  isPT = true,
  cartItems = [],
  total = 0,
  shipping = null,
} = {}) => {
  let mensagemTexto = `${getCustomerGreeting(nomeCliente, isPT)}\n\n`;

  mensagemTexto += isPT
    ? `Estou no *checkout* e preciso de ajuda para finalizar:\n\n`
    : `I'm at *checkout* and need help completing the order:\n\n`;

  if (cartItems.length > 0) {
    mensagemTexto += formatCartItemsForWhatsApp(cartItems, isPT);
  }

  mensagemTexto += isPT
    ? `*Total do pedido:* ${formatBRL(total)}\n`
    : `*Order total:* ${formatBRL(total)}\n`;

  if (shipping) {
    mensagemTexto += isPT
      ? `*Frete:* ${shipping.name} — ${formatBRL(shipping.price)}\n`
      : `*Shipping:* ${shipping.name} — ${formatBRL(shipping.price)}\n`;
  }

  mensagemTexto += isPT
    ? `\nPodem me orientar sobre pagamento ou entrega?\n\nObrigado!`
    : `\nCan you help with payment or delivery?\n\nThank you!`;

  return sanitizeWhatsAppText(mensagemTexto);
};

/**
 * Mensagem de envio de comprovante (PIX direto) — voz do cliente
 */
export const buildOrderWhatsAppMessage = ({
  nomeCliente = 'visitante',
  isPT = true,
  orderReference = '',
  total = 0,
  contextoAdicional = '',
} = {}) => {
  let mensagemTexto = `${getCustomerGreeting(nomeCliente, isPT)}\n\n`;

  mensagemTexto += isPT
    ? `Estou enviando o comprovante do meu pagamento via *PIX direto*.\n\n`
    : `I'm sending the proof of payment via *direct PIX*.\n\n`;

  if (orderReference) {
    mensagemTexto += isPT
      ? `🧾 *Pedido:* ${orderReference}\n`
      : `🧾 *Order:* ${orderReference}\n`;
  }
  if (Number.isFinite(Number(total)) && Number(total) > 0) {
    mensagemTexto += isPT
      ? `💰 *Valor:* ${formatBRL(total)}\n`
      : `💰 *Amount:* ${formatBRL(total)}\n`;
  }

  if (contextoAdicional) {
    mensagemTexto += `\n${contextoAdicional}\n`;
  }

  mensagemTexto += isPT
    ? `\nAguardo a confirmação. Obrigado!`
    : `\nWaiting for confirmation. Thank you!`;

  return sanitizeWhatsAppText(mensagemTexto);
};

/**
 * Gera link do WhatsApp com contexto específico
 * @param {Object} options - Opções
 * @param {string} options.context - Contexto da página (catalog, product, cart, checkout)
 * @param {string} options.phoneNumber - Número customizado (opcional)
 * @param {Object} options.messageOptions - Opções para construir a mensagem
 * @returns {string} URL completa do WhatsApp
 */
export const generateContextualWhatsAppLink = ({
  context = WHATSAPP_CONTEXT.CATALOG,
  phoneNumber = null,
  messageOptions = {}
} = {}) => {
  const numero = getWhatsAppNumber(context, phoneNumber);
  let mensagemTexto = '';

  switch (context) {
    case WHATSAPP_CONTEXT.PRODUCT:
      if (messageOptions.product) {
        mensagemTexto = buildProductWhatsAppMessage(
          messageOptions.product,
          messageOptions.nomeCliente || 'visitante',
          messageOptions.isPT !== false
        );
      } else if (messageOptions.contextoAdicional) {
        mensagemTexto = messageOptions.contextoAdicional;
      } else {
        mensagemTexto = buildWhatsAppMessage({
          ...messageOptions,
          contextoAdicional:
            messageOptions.contextoAdicional ||
            (messageOptions.isPT === false
              ? "I'm interested in a part."
              : 'Tenho interesse em uma peça.'),
        });
      }
      break;
    case WHATSAPP_CONTEXT.CART:
      mensagemTexto = buildCartWhatsAppMessage({
        nomeCliente: messageOptions.nomeCliente || 'visitante',
        isPT: messageOptions.isPT !== false,
        cartItems: messageOptions.cartItems || [],
        total: messageOptions.total || 0,
        contextoAdicional: messageOptions.contextoAdicional || '',
      });
      break;
    case WHATSAPP_CONTEXT.CHECKOUT:
      mensagemTexto = buildCheckoutWhatsAppMessage({
        nomeCliente: messageOptions.nomeCliente || "visitante",
        isPT: messageOptions.isPT !== false,
        cartItems: messageOptions.cartItems || [],
        total: messageOptions.total || 0,
        shipping: messageOptions.shipping || null
      });
      break;
    case WHATSAPP_CONTEXT.ORDER:
      mensagemTexto = buildOrderWhatsAppMessage({
        nomeCliente: messageOptions.nomeCliente || "visitante",
        isPT: messageOptions.isPT !== false,
        orderReference: messageOptions.orderReference || "",
        total: messageOptions.total || 0,
        contextoAdicional: messageOptions.contextoAdicional || ""
      });
      break;
    default:
      mensagemTexto = buildWhatsAppMessage(messageOptions);
  }

  return buildWhatsAppChatUrl(numero, mensagemTexto);
};

/**
 * Função de tracking para analytics (genérica, compatível com diferentes sistemas)
 * @param {string} event - Nome do evento
 * @param {Object} data - Dados do evento
 */
export const trackWhatsAppClick = (event = 'whatsapp_clicked', data = {}) => {
  // Google Analytics 4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', event, {
      event_category: 'WhatsApp',
      ...data
    });
  }

  // Google Analytics Universal
  if (typeof window !== 'undefined' && window.ga) {
    window.ga('send', 'event', 'WhatsApp', event, JSON.stringify(data));
  }

  // Facebook Pixel
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'Contact', {
      content_name: 'WhatsApp',
      ...data
    });
  }

  // DataLayer (GTM)
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: event,
      eventCategory: 'WhatsApp',
      ...data
    });
  }

  // Console log para debug (apenas em desenvolvimento)
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 WhatsApp Analytics:', { event, data });
  }
};

